import { Row, Col, Upload, Typography, Card } from "antd";
import type { UploadFile, UploadProps } from "antd/es/upload/interface";
import { FilePdfOutlined } from "@ant-design/icons";
import { useState } from "react";
import ImageUpload from "./ImageUpload";
import { UPLOAD_IMAGE, UPLOAD_PDF } from "../../services/apiEndPoints";
import axios from "../../services/axiosInstance";
import FullscreenLoader from "./FullPageLoader";
import { toastError, toastSuccess } from "../../utils/toast";

const { Text } = Typography;

interface DualUploadProps {
  image?: UploadFile | null;
  pdf?: UploadFile | null;
  onImageChange?: (file: UploadFile | null) => void;
  onPdfChange?: (file: UploadFile | null) => void;
}

const CARD_HEIGHT = 450;

export const imageInfo = (data: any) => {
  const { document_type, expiry_date, companyName, documentId, ...other_data } =
    data;
  return Object.keys(other_data).map((key) => ({
    key,
    value: other_data[key],
  }));
};

export const snakeToCapitalCase = (str: string) =>
  str
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");

const DualUpload: React.FC<DualUploadProps> = ({
  image,
  pdf,
  onImageChange,
  onPdfChange,
}) => {
  const [pdfFileList, setPdfFileList] = useState<UploadFile[]>(
    pdf ? [pdf] : []
  );
  const [, setProcessedDetails] = useState<any>(null);

  const [imageUploading, setImageUploading] = useState(false);

  /* -------------------- PDF Upload -------------------- */

  const beforePdfUpload: UploadProps["beforeUpload"] = (file) => {
    if (file.type !== "application/pdf") {
      toastError("Only PDF files are allowed");
      return Upload.LIST_IGNORE;
    }

    if (file.size / 1024 / 1024 > 10) {
      toastError("PDF must be smaller than 10MB");
      return Upload.LIST_IGNORE;
    }

    return false;
  };

  const handlePdfChange: UploadProps["onChange"] = async ({ fileList }) => {
    setPdfFileList(fileList);
    const file = fileList[0] || null;
    onPdfChange?.(file);

    setProcessedDetails(null);
    setImageUploading(true);
    onImageChange?.(null);

    if (!file) return;

    try {
      const formData = new FormData();
      formData.append("file", file.originFileObj as Blob, file.name);

      const res = await fetch(UPLOAD_PDF, {
        method: "POST",
        body: formData,
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });

      const result = await res.json();
      setProcessedDetails(result?.data || null);
      setImageUploading(false);

      if (result && result.message) {
        toastError(result.message);
        return;
      }

      toastSuccess("Uploaded successfully");
        await new Promise((resolve) => setTimeout(resolve, 2000));
    } catch (err) {
      console.error("[DualUpload][PDF] upload threw", err);
      toastError("PDF upload failed");
    }
  };

  const handlePdfRemove = () => {
    setPdfFileList([]);
    onPdfChange?.(null);
  };

  /* -------------------- Image Upload -------------------- */

  const handleImageChange = async (file: UploadFile | null) => {
    onImageChange?.(file);
    if (!file) return;

    setImageUploading(true);
    setProcessedDetails(null);
    handlePdfRemove();

    try {
      const formData = new FormData();
      formData.append("image", file.originFileObj as Blob);

      const result = await axios.post(UPLOAD_IMAGE, formData);
      setProcessedDetails(result?.data?.data || null);

      if (result && result.data && result.data.message) {
        toastError(result.data.message);
        return;
      }

      console.log("Image upload result:", result);

      toastSuccess("Uploaded successfully");
      await new Promise((resolve) => setTimeout(resolve, 2000));
    } catch (err) {
      console.error("[DualUpload][Image] upload threw", err);
      toastError("Image upload failed");
    } finally {
      setImageUploading(false);
    }
  };

  /* -------------------- UI -------------------- */

  return (
    <>
      <FullscreenLoader
        visible={imageUploading}
        text="Uploading & Extracting Details..."
      />
      <Row justify="center" gutter={24}>
        {/* Image Upload */}
        <Col xs={24} md={12}>
          <Card
            title="Image Upload"
            style={{ height: CARD_HEIGHT }}
            bodyStyle={{
              height: "100%",
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
              gap: 16,
            }}
          >
            <ImageUpload
              value={image}
              onChange={handleImageChange}
              disabled={imageUploading}
            />
          </Card>
        </Col>

        {/* PDF Upload */}
        <Col xs={24} md={12}>
          <Card
            title="PDF Upload"
            style={{ height: CARD_HEIGHT }}
            bodyStyle={{
              height: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Upload
              accept="application/pdf"
              maxCount={1}
              fileList={pdfFileList}
              beforeUpload={beforePdfUpload}
              onChange={handlePdfChange}
              onRemove={handlePdfRemove}
            >
              <div
                style={{
                  width: 220,
                  height: 220,
                  border: "1px dashed #434343",
                  background: "#1f293705",
                  borderRadius: 12,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                  transition: "all 0.2s",
                }}
              >
                <FilePdfOutlined style={{ fontSize: 42 }} />
                <Text style={{ marginTop: 12 }}>Click to upload PDF</Text>
                <Text type="secondary">Max size 10MB</Text>
              </div>
            </Upload>
          </Card>
        </Col>
      </Row>
    </>
  );
};

export default DualUpload;
